import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, AlertTriangle, Clock, Database, Layers, ListChecks, Play, RefreshCw, Search, Square, Wrench } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import databricksIcon from '../assets/icons/databricks.png';

const DatabricksIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [exampleJob, setExampleJob] = useState('');
  const [exampleChannel, setExampleChannel] = useState('');

  const [workspaceUrl, setWorkspaceUrl] = useState('');
  const [pollInterval, setPollInterval] = useState<number | null>(null);
  const [lastPollAt, setLastPollAt] = useState<string | null>(null);

  const [connectWorkspaceUrl, setConnectWorkspaceUrl] = useState('');
  const [connectApiToken, setConnectApiToken] = useState('');

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getDatabricksStatus();
      if (data?.connected) {
        setConnected(true);
        setWorkspaceUrl(data.workspace_url || '');
        setPollInterval(data.poll_interval ?? null);
        setLastPollAt(data.last_poll_at || null);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectDatabricks({
        workspace_url: connectWorkspaceUrl.trim(),
        api_token: connectApiToken,
      });
      setSuccessMsg('Databricks connected successfully!');
      setConnectApiToken('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectDatabricks();
      setConnected(false);
      setSuccessMsg('Databricks disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testDatabricks();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Databricks (${data.job_count ?? 0} job${data.job_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}
      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={databricksIcon} alt="Databricks" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  Databricks
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>React to failed job runs, unexpected cluster terminations, and DLT pipeline failures; repair runs, manage clusters and warehouses, run SQL checks, and run AI investigations against Databricks</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {workspaceUrl && (
                <div className="min-w-0">
                  <span className="text-muted-foreground">Workspace</span>
                  <p className="font-medium truncate">{workspaceUrl}</p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Poll Interval</span>
                <p className="font-medium">{pollInterval != null ? (pollInterval >= 60 ? `${Math.round(pollInterval / 60)}m` : `${pollInterval}s`) : '5m'}</p>
              </div>
              {lastPollAt && (
                <div>
                  <span className="text-muted-foreground">Last Poll</span>
                  <p className="font-medium">{new Date(lastPollAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these Databricks triggers and actions in your workflows to automate job failure recovery, cluster lifecycle, pipeline reruns, cost control, and data-quality checks.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers (polling)</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Job Run Failed / Succeeded', desc: 'Triggers when a job run terminates, scoped to specific jobs.', Icon: AlertTriangle },
                  { name: 'Job Run Exceeded Duration', desc: 'Triggers when a running job passes a duration threshold.', Icon: Clock },
                  { name: 'Cluster Terminated Unexpectedly', desc: 'Triggers on error terminations (Spark errors, cloud failures).', Icon: XCircle },
                  { name: 'DLT Pipeline Update Failed', desc: 'Triggers when a Delta Live Tables update fails.', Icon: Layers },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Run Job / Cancel Run', desc: 'Trigger a job run with parameters, or cancel a runaway run.', Icon: Play },
                  { name: 'Repair Run', desc: 'Re-run only the failed tasks of a run, reusing successful results.', Icon: Wrench },
                  { name: 'Get Run / Run Output', desc: 'Fetch run state, errors, stack traces, and notebook output.', Icon: ListChecks },
                  { name: 'Start / Restart / Terminate Cluster', desc: 'Cluster lifecycle for recovery and cost control.', Icon: RefreshCw },
                  { name: 'Start / Stop Pipeline Update', desc: 'Re-run or stop Delta Live Tables pipeline updates.', Icon: Layers },
                  { name: 'Execute SQL', desc: 'Run data-quality checks and queries on a SQL warehouse.', Icon: Database },
                  { name: 'Start / Stop SQL Warehouse', desc: 'Warehouse lifecycle for scheduled cost control.', Icon: Square },
                  { name: 'Investigate Databricks', desc: 'AI investigates jobs, runs, clusters, and pipelines (read-only).', Icon: Search },
                  { name: 'List Jobs / Clusters / Pipelines / Warehouses', desc: 'Enumerate workspace resources for downstream steps.', Icon: Layers },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When the{' '}
              <input
                value={exampleJob}
                onChange={e => setExampleJob(e.target.value)}
                placeholder="job name"
                style={{ width: `${Math.max(exampleJob.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}Databricks job fails, fetch the run output, repair the failed tasks, and post the error summary to{' '}
              <input
                value={exampleChannel}
                onChange={e => setExampleChannel(e.target.value)}
                placeholder="#channel"
                style={{ width: `${Math.max(exampleChannel.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}on Slack.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleJob || !exampleChannel}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When the ${exampleJob} Databricks job fails, fetch the run output, repair the failed tasks, and post the error summary to ${exampleChannel} on Slack.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={databricksIcon} alt="Databricks" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle>Databricks</CardTitle>
                <CardDescription>Connect Databricks to react to failed job runs, cluster terminations, and DLT pipeline failures, repair runs, manage clusters and SQL warehouses, run data-quality SQL checks, and run AI investigations in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="databricks-workspace-url" className="text-sm">Workspace URL</Label>
              <Input
                id="databricks-workspace-url"
                value={connectWorkspaceUrl}
                onChange={e => setConnectWorkspaceUrl(e.target.value)}
                placeholder="https://dbc-a1b2c3d4-e5f6.cloud.databricks.com"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Your workspace's base URL, as shown in the browser address bar (AWS: <code className="bg-muted px-1 rounded text-[11px] font-mono">dbc-*.cloud.databricks.com</code>, Azure: <code className="bg-muted px-1 rounded text-[11px] font-mono">adb-*.azuredatabricks.net</code>, GCP: <code className="bg-muted px-1 rounded text-[11px] font-mono">*.gcp.databricks.com</code>).
              </p>
            </div>
            <div>
              <Label htmlFor="databricks-token" className="text-sm">Personal Access Token</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Kestrel authenticates to the Databricks REST API with a <strong className="text-foreground">personal access token</strong>. In your workspace, open <strong className="text-foreground">Settings → Developer → Access tokens → Generate new token</strong>. Under <strong className="text-foreground">Scope</strong>, choose <strong className="text-foreground">Other APIs</strong> and select the <strong className="text-foreground">jobs</strong>, <strong className="text-foreground">clusters</strong>, <strong className="text-foreground">pipelines</strong>, <strong className="text-foreground">sql</strong>, and <strong className="text-foreground">query-history</strong> API scopes (avoid "all APIs"). A service-principal token is recommended for production.
              </p>
              <Input
                id="databricks-token"
                type="password"
                value={connectApiToken}
                onChange={e => setConnectApiToken(e.target.value)}
                placeholder="dapi..."
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Stored encrypted and only used to authenticate requests to the Databricks REST API.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !connectApiToken || !connectWorkspaceUrl.trim()}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Databricks
            </Button>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default DatabricksIntegration;
